// ============================================================
// FILE: BuyerDashboard.js
// LOCATION: C:\gardenmarket\web\src\pages\buyer\BuyerDashboard.js
// ============================================================
// PURPOSE:
//   Buyer's "My Jobs" page. Master-detail layout.
//
// STATUS POLICY:
//   'matched' is hidden from buyer-facing filters — it's an
//   internal system state. The API still filters by it if
//   explicitly requested, but the buyer UI doesn't expose it.
//   'matched' jobs appear under the "All" view labeled as
//   "Open — gardeners notified".
// ============================================================

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import client from '../../api/client';
import MasterDetailLayout from '../../components/MasterDetailLayout';
import JobRow from './JobRow';
import JobDetailContent from './JobDetailContent';
import { colors, radius, space } from '../../theme/theme';

export default function BuyerDashboard() {
    const [jobs,    setJobs]    = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter,  setFilter]  = useState('');
    const [selectedJobId, setSelectedJobId] = useState(null);

    const loadJobs = () => {
        return client.get('/jobs/my', { params: { status_code: filter || undefined } })
            .then(r => setJobs(r.data.jobs));
    };

    useEffect(() => {
        setLoading(true);
        loadJobs().catch(console.error).finally(() => setLoading(false));
    }, [filter]);

    const handleJobChanged = () => {
        loadJobs().catch(console.error);
    };

    const items = jobs.map(j => ({ ...j, id: j.job_id }));

    // Filters — 'matched' intentionally excluded since buyers
    // shouldn't need to know about it as a separate state
    const filters = [
        { v: '',          l: 'All' },
        { v: 'open',      l: 'Open' },
        { v: 'quoted',    l: 'Quotes in' },
        { v: 'accepted',  l: 'Booked' },
        { v: 'completed', l: 'Completed' },
        { v: 'reviewed',  l: 'Reviewed' },
    ];

    const listHeader = (
        <div style={styles.headerBlock}>
            <div style={styles.headerRow}>
                <h1 style={styles.title}>My Jobs</h1>
                <Link to="/buyer/post-job" style={styles.btnPost}>+ Post New Job</Link>
            </div>
            <div style={styles.filters}>
                {filters.map(f => (
                    <button
                        key={f.v}
                        style={filter === f.v ? styles.filterActive : styles.filterBtn}
                        onClick={() => setFilter(f.v)}
                    >
                        {f.l}
                    </button>
                ))}
            </div>
        </div>
    );

    if (loading) return <div style={styles.loading}>Loading your jobs...</div>;

    return (
        <div style={styles.page}>
            {jobs.length === 0 ? (
                <>
                    {listHeader}
                    <div style={styles.empty}>
                        <p style={{marginBottom:'16px'}}>No jobs found.</p>
                        <Link to="/buyer/post-job" style={styles.btnPost}>Post your first job</Link>
                    </div>
                </>
            ) : (
                <MasterDetailLayout
                    items={items}
                    selectedId={selectedJobId}
                    onSelect={setSelectedJobId}
                    listHeader={listHeader}
                    columns={['Date', 'Job', 'Status']}
                    renderRow={(job, isSelected, onClick) => <JobRow key={job.id} job={job} isSelected={isSelected} onClick={onClick} />}
                    renderDetail={() => (
                        <JobDetailContent
                            jobId={selectedJobId}
                            embedded={true}
                            onJobChanged={handleJobChanged}
                        />
                    )}
                    emptyDetailText="Select a job from the list to view its details."
                />
            )}
        </div>
    );
}

const styles = {
    page:        { maxWidth:'1300px', margin:'0 auto', padding: space.xl + ' 24px' },
    loading:     { textAlign:'center', padding:'60px', color: colors.textMuted },
    empty:       { textAlign:'center', padding:'60px', color: colors.textMuted },
    headerBlock: { padding:'16px 18px 4px' },
    headerRow:   { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' },
    title:       { margin:0, color: colors.text, fontSize:'1.2rem', fontWeight:700 },
    btnPost:     { backgroundColor: colors.brand, color:'white', padding:'8px 16px', borderRadius: radius.sm, textDecoration:'none', fontWeight:600, fontSize:'0.8rem' },
    filters:     { display:'flex', gap:'6px', marginBottom:'16px', flexWrap:'wrap' },
    filterBtn:   { padding:'5px 12px', borderRadius: radius.pill, border:`1px solid ${colors.border}`, background:'white', cursor:'pointer', fontSize:'0.74rem', color: colors.textMuted },
    filterActive:{ padding:'5px 12px', borderRadius: radius.pill, border:`1px solid ${colors.brand}`, background: colors.brand, color:'white', cursor:'pointer', fontSize:'0.74rem', fontWeight:600 },
};
